/**
 * Bootstrap 5 component initialisation inside Shadow DOM.
 *
 * Each function receives the shadowRoot so it can query and
 * instantiate components without knowing about the host element.
 */

import * as bootstrap5 from 'bootstrap';
import { patchModalForShadowDOM, ensureBackdrop } from './modal-patch.js';

// ---------------------------------------------------------------------------
// Tooltips & Popovers
// ---------------------------------------------------------------------------

export function initializeTooltips(shadowRoot) {
    shadowRoot.querySelectorAll('[data-bs-toggle="tooltip"]')
        .forEach(el => new bootstrap5.Tooltip(el));
}

export function initializePopovers(shadowRoot) {
    shadowRoot.querySelectorAll('[data-bs-toggle="popover"]')
        .forEach(el => new bootstrap5.Popover(el));
}

// ---------------------------------------------------------------------------
// Accordions
// ---------------------------------------------------------------------------

/**
 * Manually wire accordion buttons because Bootstrap's data-api
 * doesn't reach inside the Shadow DOM.
 *
 * @param {ShadowRoot} shadowRoot
 */
export function initializeAccordions(shadowRoot) {
    shadowRoot.querySelectorAll('.accordion-button').forEach(btn => {
        // Avoid double-binding if called more than once on the same element
        if (btn._accordionBound) return;
        btn._accordionBound = true;

        btn.addEventListener('click', () => {
            const targetId = btn.getAttribute('data-bs-target');
            const target = shadowRoot.querySelector(targetId);
            if (!target) return;

            // find the parent accordion (data-bs-parent)
            const parentSelector = target.getAttribute('data-bs-parent');
            if (parentSelector) {
                const parentEl = shadowRoot.querySelector(parentSelector);
                if (parentEl) {
                    // Close all other open panels in the same accordion
                    parentEl.querySelectorAll('.accordion-collapse.show').forEach(openPanel => {
                        if (openPanel !== target) {
                            bootstrap5.Collapse.getOrCreateInstance(openPanel).hide();
                        }
                    });
                }
            }

            bootstrap5.Collapse.getOrCreateInstance(target).toggle();
        });
    });
}

// ---------------------------------------------------------------------------
// Modals
// ---------------------------------------------------------------------------

/**
 * Patch the Modal class and prepare the backdrop.
 *
 * @param {ShadowRoot} shadowRoot
 */
export function initializeModals(shadowRoot) {
    patchModalForShadowDOM();
    ensureBackdrop(shadowRoot);
    registerBackdropClickHandler(shadowRoot);

}

/**
 * Close modals when clicking outside (on the backdrop area).
 * In Shadow DOM, Bootstrap's native backdrop click doesn't work,
 * so we handle it manually, but only if the modal is not static.
 *
 * @param {ShadowRoot} shadowRoot
 */
function registerBackdropClickHandler(shadowRoot) {
    shadowRoot.addEventListener('click', (e) => {
        // The .modal element acts as the backdrop overlay when shown.
        // A click directly on .modal (not on .modal-dialog or its children)
        // means the user clicked outside the dialog content.
        if (e.target.classList.contains('modal') && e.target.classList.contains('show')) {
            const modal = bootstrap5.Modal.getInstance(e.target);
            if (modal && modal._config.backdrop !== 'static') {
                modal.hide();
            }
        }
    });
}
// ---------------------------------------------------------------------------
// Dropdowns
// ---------------------------------------------------------------------------

/**
 * Manually wire dropdown toggles via shadow-root event delegation.
 *
 * Bootstrap's data-api listens on `document`. Events from inside a Shadow DOM
 * are retargeted (event.target becomes the shadow host) before they reach
 * `document`, so Bootstrap never sees the actual toggle button.
 * Popper.js positioning also fails across the shadow boundary.
 *
 * Solution: one delegated click listener on the shadow root + manual CSS-class
 * toggling, the same pattern used by initializeTabs / initializeAccordions.
 *
 * @param {ShadowRoot} shadowRoot
 */
export function initializeDropdowns(shadowRoot) {
    if (shadowRoot._dropdownsBound) return;
    shadowRoot._dropdownsBound = true;

    shadowRoot.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-bs-toggle="dropdown"]');

        if (!toggle) {
            // Outside click ? close every open dropdown
            shadowRoot.querySelectorAll('[data-bs-toggle="dropdown"].show').forEach(btn => {
                btn.classList.remove('show');
                btn.nextElementSibling?.classList.remove('show');
            });
            return;
        }

        event.preventDefault();
        const menu = toggle.nextElementSibling;
        if (!menu?.classList.contains('dropdown-menu')) return;

        const isOpen = menu.classList.contains('show');

        // Close any other open dropdown first
        shadowRoot.querySelectorAll('[data-bs-toggle="dropdown"].show').forEach(btn => {
            if (btn !== toggle) {
                btn.classList.remove('show');
                btn.nextElementSibling?.classList.remove('show');
            }
        });

        toggle.classList.toggle('show', !isOpen);
        menu.classList.toggle('show', !isOpen);
    });
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

/**
 * Manually wire tab buttons because Bootstrap's data-api
 * doesn't reach inside the Shadow DOM.
 *
 * @param {ShadowRoot} shadowRoot
 */
export function initializeTabs(shadowRoot) {
    shadowRoot.querySelectorAll('[data-bs-toggle="tab"]').forEach(btn => {
        if (btn._tabBound) return;
        btn._tabBound = true;

        btn.addEventListener('click', () => {
            const targetSelector = btn.getAttribute('data-bs-target');
            const targetEl = shadowRoot.querySelector(targetSelector);
            if (!targetEl) return;

            // Deactivate all tabs and panels in the same group
            const tabList = btn.closest('[role="tablist"]');
            if (tabList) {
                tabList.querySelectorAll('[data-bs-toggle="tab"]').forEach(t => {
                    t.classList.remove('active');
                    t.setAttribute('aria-selected', 'false');
                });
            }
            shadowRoot.querySelectorAll('.tab-pane').forEach(pane => {
                pane.classList.remove('show', 'active');
            });

            // Activate the clicked tab and his panel
            btn.classList.add('active');
            btn.setAttribute('aria-selected', 'true');
            targetEl.classList.add('show', 'active');

            // Notify components interested (ex: FullCalendar resize)
            btn.dispatchEvent(new CustomEvent('tab-shown', {
                bubbles: true,
                detail: { tabButton: btn, tabPanel: targetEl }
            }));
        });
    });
}

// ---------------------------------------------------------------------------
// All-in-one
// ---------------------------------------------------------------------------

/**
 * Initialise every Bootstrap component type.
 *
 * @param {ShadowRoot} shadowRoot
 */
export function initializeAll(shadowRoot) {
    initializeTooltips(shadowRoot);
    initializePopovers(shadowRoot);
    initializeModals(shadowRoot);
    initializeAccordions(shadowRoot);
    initializeTabs(shadowRoot);
    initializeDropdowns(shadowRoot);
}